import SessionContext from '@providers/Session/SessionContext';
import { Col, Empty, Row, Select, Spin } from 'antd';
import { Area } from '@ant-design/plots';
import React, {
  CSSProperties,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import useSearchParams from '@hooks/useSearchParams';
import { formatBRNumber } from '@utils/formatters';
import { valueType } from 'antd/lib/statistic/utils';
import { useGraphColors } from '@hooks/useGraphColors';
import StatusMenu from '@components/StatusMenu';
import { StatusMenuContainer } from '@components/Table/styles';
import { useOnlineDevices } from '@hooks/useOnlineDevices';
import { CreateAlarmButton } from '@components/CreateAlarmButton/CreateAlarmButton';
import { CreateAlarmSteps } from '@components/Insights/CreateAlarm/CreateAlarmSteps';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import * as S from './styles';

interface IStyleCss {
  [key: string]: CSSProperties;
}

interface ICharDataResponse {
  results: {
    series?: {
      columns: string[];
      name: string;
      tags: {
        operadora: string;
        produto: string;
        tecnologia: string;
      };
      values: number[][];
    }[];
    statement_id: number;
  }[];
  totalValue: number;
}

const urlsNames: { [key: string]: string } = {
  'by-product': '/service-proxy/dashboard/simcard_online/grafico/produto',
  'by-technology': '/service-proxy/dashboard/simcard_online/grafico/tecnologia',
  'by-operator': '/service-proxy/dashboard/simcard_online/grafico/operadora',
};

interface IProps {
  currentDevice: string;
  onViewChange: (view: string) => void;
}

const DEFAULT_FORMDATA = {
  groupByTime: '5m',
  lastTime: '7d',
};

const MIN_VALUE = 0;

export const SimcardsDashboard = ({ currentDevice, onViewChange }: IProps) => {
  const [openCreateAlarmModal, setOpenCreateAlarmModal] = useState(false);

  const { t, i18n } = useTranslation([
    'date-picker',
    'simcards-online-product',
  ]);

  const currentLanguage = i18n.language;

  const { setSearchParams, clearSearchParams, searchParams } = useSearchParams({
    extraParams: ['lastTime'],
  });

  const [formData, setFormData] = useState({
    groupByTime: searchParams?.groupByTime || DEFAULT_FORMDATA?.groupByTime,
    lastTime: searchParams?.lastTime || DEFAULT_FORMDATA?.lastTime,
  });

  const { statusItems, activeStatusItem, handleStatusCardSelect } =
    useOnlineDevices(onViewChange);

  const { customersSwitch, theme, graphThemeKey } = useContext(SessionContext);
  const graphColors = useGraphColors();
  const colors = graphColors[graphThemeKey];

  const { data, isLoading } = useSwr<ICharDataResponse>(
    urlsNames?.[currentDevice] || urlsNames?.['by-product'],
    {
      params: {
        lastTime: formData?.lastTime,
        groupByTime: formData?.groupByTime,
      },
      headers: {
        customerId: customersSwitch?.selected,
      },
    },
    {
      cancelPrevious: false,
    },
  );

  const styles: IStyleCss = {
    titleCard: { lineHeight: '40px', fontSize: '1rem' },
    blank: { background: 'transparent' },
    formSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    select: { width: '15rem' },
    spinner: {
      width: '100%',
      padding: '5rem 1rem',
      margin: 16,
    },
    charTitle: {
      margin: '1rem auto 0',
    },
    paragraph: { margin: 0, width: '100%', textAlign: 'end' },
    rowCard: {
      height: '50%',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    labelCard: { fontSize: '25px', lineHeight: '1.2' },
    valueCard: {
      textAlign: 'end',
      fontSize: '36px',
      fontWeight: '500',
      lineHeight: '1.2',
    },
    statusMenu: { margin: '0.5rem 0' },
    questionIcon: { color: '#fff' },
    cardContainer: {
      marginTop: '16px',
    },
  };

  const getCharData = () => {
    if (!data || !data.results || !data.results[0] || !data.results[0].series) {
      return [];
    }
    const charData = data?.results[0]?.series
      ?.map(doc =>
        doc?.values?.map(value => {
          const listItem: { [key: string]: string | number } = {
            date: value[0],
            value: value[1] || 0,
            category:
              doc?.tags?.produto ||
              doc?.tags?.tecnologia ||
              doc?.tags?.operadora,
          };

          return listItem;
        }),
      )
      .flat();
    return charData || [];
  };

  const formatStatisticValue = useCallback(
    (value: valueType) => formatBRNumber(value),
    [],
  );

  const productData = useMemo(() => {
    const initialValue = 0;
    const charData = getCharData();
    const cardsData =
      data?.results?.[0]?.series?.map(e => ({
        title: e?.tags?.produto || e?.tags?.tecnologia || e?.tags?.operadora,
        value:
          e?.values?.[e?.values?.length - 1]?.[1] ||
          e?.values?.[e?.values?.length - 2]?.[1] ||
          0,
      })) || [];
    const total = Object.values(cardsData).reduce(
      (sum, card) => sum + card?.value,
      initialValue,
    );

    return { cardsData, charData, total };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const { lastTimeOptions } = useMemo(
    () => ({
      lastTimeOptions: [
        { label: t('last 10 minutes', { minutes: 10 }), value: '10m' },
        { label: t('last 15 minutes', { minutes: 15 }), value: '15m' },
        { label: t('last 30 minutes', { minutes: 30 }), value: '30m' },
        { label: t('last 1 hour', { days: 1 }), value: '1h' },
        { label: t('last 24 hour', { hours: 24 }), value: '24h' },
        { label: t('last-week', { days: 7 }), value: '7d' },
        { label: t('last-two-weeks', { days: 14 }), value: '14d' },
        { label: t('last-three-weeks', { days: 21 }), value: '21d' },
        { label: t('last-month', { days: 30 }), value: '30d' },
      ],
    }),
    [t],
  );

  const formatAxisY = (value: string) => {
    const million = 1000000;
    const thousand = 1000;
    const formatedValue = Number(value);
    if (formatedValue >= million) {
      return `${formatedValue / million}mil`;
    }
    if (formatedValue >= thousand) {
      return `${formatedValue / thousand}k`;
    }
    return formatedValue;
  };

  const getSelectValue = (searchParam: string, defaultValue: string) => {
    const isValidValue = lastTimeOptions?.find(
      option => option?.value === searchParam,
    );

    if (!isValidValue && !!searchParam) {
      clearSearchParams();
      return defaultValue;
    }

    return isValidValue ? searchParam : defaultValue;
  };

  const hasData = useMemo(() => !!productData?.charData?.length, [productData]);

  const handleSelectedCard = useCallback(() => {
    // Sem operação: Intencionalmente vazio para desabilitar o comportamento de seleção do card
  }, []);

  const renderStatCards = useMemo(() => {
    if (!hasData || isLoading) return null;

    return (
      <div className="status-menu-display-items-container">
        <StatusMenu
          // @ts-expect-error
          data={productData?.cardsData?.map(product => ({
            label: product.title,
            value: product.title.toLowerCase().replace(/\s+/g, '-'),
            total: product.value || '0',
            color: 'var(--primary-color)',
          }))}
          handleSelectedCard={handleSelectedCard}
        />
      </div>
    );
  }, [hasData, isLoading, productData?.cardsData, handleSelectedCard]);

  const getDateFormat = useMemo(() => {
    return currentLanguage === 'en' ? 'MM/DD HH:mm' : 'DD/MM HH:mm';
  }, [currentLanguage]);

  const areaConfig = useMemo(
    () => ({
      style: { height: 400 },
      data: productData?.charData,
      xField: 'date',
      yField: 'value',
      seriesField: 'category',
      isStack: true,
      xAxis: {
        type: 'timeCat',
        mask: getDateFormat,
        label: {
          autoHide: true,
          autoRotate: false,
          style: {
            fill: theme?.colors?.text || '#000',
          },
        },
      },
      yAxis: {
        label: {
          formatter: (val: string) => formatAxisY(val),
          style: {
            fill: theme?.colors?.text || '#000',
          },
        },
        min: 0,
      },
      legend: {
        position: 'bottom' as const,
        itemName: {
          style: {
            fill: theme?.colors?.text || '#000',
          },
        },
      },
      smooth: true,
      areaStyle: { fillOpacity: 0.6 },
      color: colors,
      tooltip: {
        shared: true,
        showCrosshairs: true,
        formatter: (datum: any) => {
          const date = new Date(datum.date);
          const formattedDate = date.toLocaleDateString(
            currentLanguage === 'en' ? 'en-US' : 'pt-BR',
            {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            },
          );
          return {
            name: datum.category,
            value: formatBRNumber(datum.value),
            title: formattedDate,
          };
        },
      },
      interactions: [{ type: 'element-active' }],
    }),
    [
      productData?.charData,
      getDateFormat,
      theme?.colors?.text,
      colors,
      currentLanguage,
    ],
  );

  return (
    <Row gutter={[16, 16]} justify="end">
      <Col xs={24}>
        <S.CardContainer
          title={
            <div className="card-title">
              <Row
                align="middle"
                justify="space-between"
                className="card-title-row"
              >
                <Row align="middle">
                  <div className="card-title-name">
                    {t('simcards-online-product:chart-title')}
                  </div>
                  <div className="total-value">
                    {!isLoading ? (
                      <>
                        <span className="total-label">
                          {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                          ({t('simcards-online-product:total')}{' '}
                          {formatStatisticValue(
                            productData?.total || MIN_VALUE,
                          )}
                          )
                        </span>
                      </>
                    ) : (
                      <Spin size="small" />
                    )}
                  </div>
                </Row>
                <div>
                  <Select
                    onChange={lastTime => {
                      setFormData(state => ({ ...state, lastTime }));
                      setSearchParams({
                        lastTime,
                      });
                    }}
                    value={getSelectValue(
                      searchParams?.lastTime,
                      formData?.lastTime,
                    )}
                    options={lastTimeOptions}
                    style={styles.select}
                    className="card-select-filter"
                  />
                </div>
              </Row>
            </div>
          }
        >
          <StatusMenuContainer>
            <StatusMenu
              data={statusItems}
              itemActive={activeStatusItem}
              handleSelectedCard={handleStatusCardSelect}
              additionalContent={isLoading ? null : renderStatCards}
            />
          </StatusMenuContainer>

          {(() => {
            if (isLoading) {
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                  }}
                >
                  <Spin
                    style={{ width: '100%', padding: '5rem 1rem', margin: 16 }}
                  />
                </div>
              );
            }

            if (hasData) {
              return (
                <S.DashboardCardContainer>
                  {currentDevice !== 'by-product' && (
                    <RoleChecker
                      role={rolesConstants.INSIGHTS_ALARM_MANAGE_CONFIGURATION}
                    >
                      <CreateAlarmButton
                        tooltipPosition="leftTop"
                        style={{ position: 'absolute', right: 10, top: 8 }}
                        openCreateAlarmModal={
                          () => setOpenCreateAlarmModal(true)
                          // eslint-disable-next-line react/jsx-curly-newline
                        }
                      />
                    </RoleChecker>
                  )}
                  <Area {...areaConfig} />
                </S.DashboardCardContainer>
              );
            }

            return (
              <Empty
                description={t('simcards-online-product:notFoundMessage')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            );
          })()}
        </S.CardContainer>
      </Col>
      <CreateAlarmSteps
        visible={openCreateAlarmModal}
        onClose={() => setOpenCreateAlarmModal(false)}
        preSelectedValues={{
          tipoGrafico: currentDevice || 'by-product',
        }}
        readOnlyFields={['tipoGrafico']}
      />
    </Row>
  );
};
